import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';

export default function NewTaskPage({ onSuccess, onCancel } = {}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm();

  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [categoryName, setCategoryName] = useState('');
  const [categorySubmitting, setCategorySubmitting] = useState(false);
  const [categoryMessage, setCategoryMessage] = useState('');
  const [categoryMessageType, setCategoryMessageType] = useState('info');

  // Admin kategori yönetimi
  const [catMgmtOpen, setCatMgmtOpen] = useState(false);
  const [editingCatId, setEditingCatId] = useState(null);
  const [editingCatName, setEditingCatName] = useState('');
  const [catMgmtBusy, setCatMgmtBusy] = useState({});
  const [catMgmtError, setCatMgmtError] = useState('');
  const [deletingCatId, setDeletingCatId] = useState(null);

  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [newCustomerIsDomestic, setNewCustomerIsDomestic] = useState(null);
  const [duplicateList, setDuplicateList] = useState([]);
  const [customerSubmitting, setCustomerSubmitting] = useState(false);
  const [customerMessage, setCustomerMessage] = useState('');
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);

  const [visitOutputText, setVisitOutputText] = useState('');
  const [visitOutputVisible, setVisitOutputVisible] = useState(false);
  const [visitPreviewLoading, setVisitPreviewLoading] = useState(false);
  const [visitDetailLoading, setVisitDetailLoading] = useState(false);
  const [visitAiError, setVisitAiError] = useState('');

  const [customerSearch, setCustomerSearch] = useState('');
  const [customerDomesticFilter, setCustomerDomesticFilter] = useState('all');
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [selectedCustomerName, setSelectedCustomerName] = useState('');
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);

  const parentType = watch('parent_type');
  const descriptionWatch = watch('description');
  const assigneeId = watch('assignee_id');
  const categoryId = watch('category_id');
  const selectedUser = users.find((u) => String(u.id) === String(assigneeId));

  const filteredCategories = useMemo(
    () => categories.filter((c) => c.parent_type === parentType),
    [categories, parentType]
  );

  const selectedCategory = filteredCategories.find((c) => String(c.id) === String(categoryId));
  const isCustomerVisit =
    parentType === 'customer' &&
    selectedCategory?.name?.toLowerCase().includes('ziyaret');

  const loadCategories = async () => {
    const { data } = await api.getCategories();
    setCategories(Array.isArray(data) ? data : data.results || []);
  };

  const loadUsers = async () => {
    const { data } = await api.getUsers();
    setUsers(Array.isArray(data) ? data : data.results || []);
  };

  const loadCustomers = async () => {
    const { data } = await api.getCustomers();
    setCustomers(Array.isArray(data) ? data : data.results || []);
  };

  useEffect(() => {
    loadCategories().catch(() => {});
    loadUsers().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (parentType === 'customer') {
      loadCustomers().catch(() => {});
    } else {
      setSelectedCustomerId('');
      setSelectedCustomerName('');
      setCustomerSearch('');
      setCustomerDropdownOpen(false);
      setCustomerDomesticFilter('all');
      setValue('customer_id', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentType]);

  useEffect(() => {
    setValue('customer_id', selectedCustomerId || '', { shouldValidate: true });
  }, [selectedCustomerId, setValue]);

  const filteredCustomersForPicker = useMemo(() => {
    let list = customers;
    if (customerDomesticFilter === 'domestic') {
      list = list.filter((c) => c.is_domestic === true);
    } else if (customerDomesticFilter === 'foreign') {
      list = list.filter((c) => c.is_domestic === false);
    }
    const q = customerSearch.trim().toLowerCase();
    if (q) {
      list = list.filter((c) => (c.name || '').toLowerCase().includes(q));
    }
    return list;
  }, [customers, customerDomesticFilter, customerSearch]);

  useEffect(() => {
    if (!isCustomerVisit) {
      setVisitOutputText('');
      setVisitOutputVisible(false);
      setVisitAiError('');
    }
  }, [isCustomerVisit]);

  useEffect(() => {
    if (!customerModalOpen || !newCustomerName.trim()) {
      setDuplicateList([]);
      return;
    }
    const timeout = setTimeout(async () => {
      setCheckingDuplicate(true);
      try {
        const { data } = await api.checkDuplicate(newCustomerName.trim());
        setDuplicateList(data.duplicates || []);
      } catch {
        setDuplicateList([]);
      } finally {
        setCheckingDuplicate(false);
      }
    }, 350);
    return () => clearTimeout(timeout);
  }, [customerModalOpen, newCustomerName]);

  const onSubmit = async (formData) => {
    setLoading(true);
    setError('');
    try {
      const payload = {
        title: formData.title,
        category_id: Number(formData.category_id),
        description: formData.description || '',
        assignee_id: Number(formData.assignee_id),
        deadline: formData.deadline,
        ...(parentType === 'customer' && formData.customer_id ? { customer: Number(formData.customer_id) } : {}),
      };
      await api.createTask(payload);
      if (onSuccess) onSuccess();
      else navigate('/satis');
    } catch (err) {
      setError(err.response?.data?.detail || 'İş oluşturulurken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const generateVisitOutput = async (mode) => {
    const desc = (descriptionWatch || '').trim();
    if (!desc) {
      setVisitAiError('Önce iş tanımını yazın.');
      return;
    }
    if (mode === 'detailed') setVisitDetailLoading(true);
    else setVisitPreviewLoading(true);
    setVisitAiError('');
    try {
      const { data } = await api.previewTaskOutput(desc, mode);
      if (data?.output_text) {
        setVisitOutputText(data.output_text);
        setVisitOutputVisible(true);
      }
    } catch (err) {
      setVisitAiError(err.response?.data?.detail || 'Özet oluşturulamadı.');
      setVisitOutputVisible(true);
    } finally {
      setVisitPreviewLoading(false);
      setVisitDetailLoading(false);
    }
  };

  const onVisitSaveAndComplete = async (formData) => {
    const out = visitOutputText.trim();
    if (out.length < 10) {
      setError('İş çıktısı en az 10 karakter olmalıdır.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload = {
        title: formData.title,
        category_id: Number(formData.category_id),
        description: formData.description || '',
        assignee_id: Number(formData.assignee_id),
        deadline: formData.deadline,
        ...(parentType === 'customer' && formData.customer_id ? { customer: Number(formData.customer_id) } : {}),
      };
      const { data: task } = await api.createTask(payload);
      const taskId = task?.id;
      if (!taskId) {
        setError('İş oluşturuldu ancak kimlik alınamadı; listeden tamamlayın.');
        setLoading(false);
        return;
      }
      await api.completeTask(taskId, { output_text: out });
      if (onSuccess) onSuccess();
      else navigate('/satis');
    } catch (err) {
      setError(err.response?.data?.detail || 'Kayıt veya tamamlama sırasında hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const submitCategoryRequest = async () => {
    if (!parentType) {
      setCategoryMessageType('error');
      setCategoryMessage('Önce iş türü seçin.');
      return;
    }
    if (!categoryName.trim()) {
      setCategoryMessageType('error');
      setCategoryMessage('Kategori adı zorunludur.');
      return;
    }
    setCategorySubmitting(true);
    setCategoryMessage('');
    try {
      const { data } = await api.requestCategory({
        name: categoryName.trim(),
        parent_type: parentType,
      });
      setCategoryMessageType('success');
      setCategoryMessage('Kategori talebi gönderildi. Onay bekliyor olarak seçildi.');
      setCategoryName('');
      await loadCategories();
      // Yeni kategoriyi otomatik seç
      if (data?.id) setValue('category_id', String(data.id));
      setCategoryModalOpen(false);
    } catch (err) {
      setCategoryMessageType('error');
      setCategoryMessage(err.response?.data?.detail || 'Kategori talebi gönderilemedi.');
    } finally {
      setCategorySubmitting(false);
    }
  };

  const submitCustomer = async () => {
    if (!newCustomerName.trim()) {
      setCustomerMessage('Müşteri adı zorunludur.');
      return;
    }
    if (newCustomerIsDomestic === null) {
      setCustomerMessage('Yurtiçi / Yurtdışı seçiniz');
      return;
    }
    setCustomerSubmitting(true);
    setCustomerMessage('');
    try {
      const { data } = await api.createCustomer({
        name: newCustomerName.trim(),
        is_domestic: newCustomerIsDomestic,
      });
      await loadCustomers();
      if (data?.id) {
        const idStr = String(data.id);
        setSelectedCustomerId(idStr);
        setSelectedCustomerName(data.name || newCustomerName.trim());
        setCustomerSearch('');
        setCustomerDropdownOpen(false);
      }
      setCustomerModalOpen(false);
      setNewCustomerName('');
      setNewCustomerIsDomestic(null);
      setDuplicateList([]);
    } catch (err) {
      setCustomerMessage(err.response?.data?.detail || 'Müşteri oluşturulamadı.');
    } finally {
      setCustomerSubmitting(false);
    }
  };

  const closeCustomerModal = () => {
    setCustomerModalOpen(false);
    setNewCustomerName('');
    setNewCustomerIsDomestic(null);
    setDuplicateList([]);
    setCustomerMessage('');
  };

  const startEditCat = (cat) => {
    setEditingCatId(cat.id);
    setEditingCatName(cat.name);
    setCatMgmtError('');
  };

  const saveEditCat = async (catId) => {
    if (!editingCatName.trim()) return;
    setCatMgmtBusy(p => ({ ...p, [catId]: true }));
    setCatMgmtError('');
    try {
      await api.updateCategory(catId, { name: editingCatName.trim() });
      setEditingCatId(null);
      await loadCategories();
    } catch (err) {
      setCatMgmtError(err.response?.data?.detail || 'Güncelleme başarısız.');
    } finally {
      setCatMgmtBusy(p => ({ ...p, [catId]: false }));
    }
  };

  const confirmDeleteCat = (cat) => {
    setDeletingCatId(cat.id);
    setCatMgmtError('');
  };

  const deleteCategory = async () => {
    if (!deletingCatId) return;
    setCatMgmtBusy(p => ({ ...p, [deletingCatId]: true }));
    setCatMgmtError('');
    try {
      await api.deleteCategory(deletingCatId);
      setDeletingCatId(null);
      await loadCategories();
    } catch (err) {
      setCatMgmtError(err.response?.data?.detail || 'Silme başarısız.');
    } finally {
      setCatMgmtBusy(p => ({ ...p, [deletingCatId]: false }));
    }
  };

  return (
    <div className="px-4 py-4">
      <form
        onSubmit={handleSubmit((fd) => {
          if (isCustomerVisit) return;
          return onSubmit(fd);
        })}
        className="space-y-4 pb-[calc(2rem+env(safe-area-inset-bottom,0px))]"
      >

        {/* İş Başlığı */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            İş Başlığı <span className="text-red-500">*</span>
          </label>
          <input
            className="input"
            style={{ fontSize: '16px' }}
            placeholder="İş başlığını girin"
            {...register('title', { required: 'Başlık zorunludur' })}
          />
          {errors.title && <p className="text-xs text-red-500 mt-1">{errors.title.message}</p>}
        </div>

        {/* İş Türü — Ana kategori */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            İş Türü <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 gap-2">
            {[
              { value: 'general', label: 'Genel İşler' },
              { value: 'customer', label: 'Müşteri İşleri' },
            ].map((type) => (
              <button
                key={type.value}
                type="button"
                onClick={() => { setValue('parent_type', type.value); setValue('category_id', ''); }}
                className={`py-3 px-4 rounded-xl border text-sm font-medium transition-colors
                  ${parentType === type.value
                    ? 'bg-brand-500 text-white border-brand-500'
                    : 'bg-white text-gray-600 border-gray-200'
                  }`}
              >
                {type.label}
              </button>
            ))}
          </div>
        </div>

        {/* Alt kategori */}
        {parentType && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Kategori <span className="text-red-500">*</span>
            </label>
            <select
              className="input"
              style={{ fontSize: '16px' }}
              {...register('category_id', { required: 'Kategori seçiniz' })}
            >
              <option value="">Kategori seçin</option>
              {filteredCategories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}{!c.is_active ? ' (onay bekliyor)' : ''}
                </option>
              ))}
            </select>
            {errors.category_id && <p className="text-xs text-red-500 mt-1">{errors.category_id.message}</p>}
            {filteredCategories.length === 0 && (
              <p className="text-xs text-amber-700 mt-1">Bu iş türünde aktif kategori yok.</p>
            )}
            <div className="mt-1 flex items-center gap-3">
              <button
                type="button"
                className="text-xs font-semibold text-brand-600"
                onClick={() => {
                  setCategoryModalOpen(true);
                  setCategoryMessage('');
                  setCategoryName('');
                }}
              >
                + Kategori Talep Et
              </button>
              {isAdmin && (
                <button
                  type="button"
                  className="text-xs font-semibold text-gray-500 border border-gray-200 rounded-lg px-2 py-0.5"
                  onClick={() => { setCatMgmtOpen(true); setCatMgmtError(''); setEditingCatId(null); setDeletingCatId(null); }}
                >
                  Kategorileri Düzenle
                </button>
              )}
            </div>
          </div>
        )}

        {/* Müşteri seçimi */}
        {parentType === 'customer' && (
          <div>
            <input
              type="hidden"
              {...register('customer_id', {
                validate: (v) =>
                  getValues('parent_type') !== 'customer' ||
                  (v != null && String(v).trim() !== '') ||
                  'Müşteri seçiniz',
              })}
            />
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Müşteri <span className="text-red-500">*</span>
            </label>
            <div className="flex rounded-xl border border-gray-200 overflow-hidden mb-2">
              {[
                { key: 'all', label: 'Tümü' },
                { key: 'domestic', label: 'Yurtiçi' },
                { key: 'foreign', label: 'Yurtdışı' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setCustomerDomesticFilter(key)}
                  className={`flex-1 py-2.5 px-2 text-sm font-medium transition-colors border-r border-gray-200 last:border-r-0
                    ${customerDomesticFilter === key
                      ? 'bg-brand-500 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">
                🔍
              </span>
              <input
                type="text"
                className="input pl-9 pr-10"
                style={{ fontSize: '16px' }}
                placeholder="Müşteri ara..."
                autoComplete="off"
                value={selectedCustomerId ? selectedCustomerName : customerSearch}
                onChange={(e) => {
                  const v = e.target.value;
                  if (selectedCustomerId) {
                    setSelectedCustomerId('');
                    setSelectedCustomerName('');
                  }
                  setCustomerSearch(v);
                  setCustomerDropdownOpen(true);
                }}
                onFocus={() => setCustomerDropdownOpen(true)}
              />
              {(selectedCustomerId || customerSearch) && (
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-lg"
                  aria-label="Temizle"
                  onClick={() => {
                    setSelectedCustomerId('');
                    setSelectedCustomerName('');
                    setCustomerSearch('');
                    setCustomerDropdownOpen(false);
                    setValue('customer_id', '', { shouldValidate: true });
                  }}
                >
                  ✕
                </button>
              )}
              {customerDropdownOpen && (
                <ul
                  className="absolute z-20 left-0 right-0 mt-1 max-h-52 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg py-1"
                  role="listbox"
                >
                  {filteredCustomersForPicker.length === 0 && (
                    <li className="px-3 py-2 text-sm text-gray-400">Sonuç yok</li>
                  )}
                  {filteredCustomersForPicker.map((c) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        className="w-full text-left px-3 py-2.5 text-sm text-gray-800 hover:bg-gray-50"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setSelectedCustomerId(String(c.id));
                          setSelectedCustomerName(c.name || '');
                          setCustomerSearch('');
                          setCustomerDropdownOpen(false);
                        }}
                      >
                        {c.name}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {errors.customer_id && <p className="text-xs text-red-500 mt-1">{errors.customer_id.message}</p>}
            <button
              type="button"
              className="mt-2 text-xs font-semibold text-brand-600"
              onClick={() => {
                setNewCustomerIsDomestic(null);
                setCustomerMessage('');
                setCustomerModalOpen(true);
              }}
            >
              + Yeni Müşteri
            </button>
          </div>
        )}

        {/* İş Tanımı */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">İş Tanımı</label>
          <div className="relative">
            <textarea
              className="input min-h-[100px] resize-none pr-10"
              style={{ fontSize: '16px' }}
              placeholder="İş tanımını girin (opsiyonel)"
              {...register('description')}
            />
            <button
              type="button"
              disabled
              title="Ses kaydı — yakında"
              className="absolute right-3 bottom-3 w-8 h-8 bg-brand-500 rounded-full flex items-center justify-center opacity-40"
            >
              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              </svg>
            </button>
          </div>
        </div>

        {isCustomerVisit && (
          <div className="space-y-2">
            <button
              type="button"
              className="btn-primary w-full sm:w-auto"
              disabled={visitPreviewLoading || visitDetailLoading || loading}
              onClick={() => generateVisitOutput('short')}
            >
              {visitPreviewLoading ? 'Oluşturuluyor...' : 'İş Çıktısı Oluştur'}
            </button>
            {visitAiError && !visitOutputVisible && (
              <p className="text-xs text-amber-700">{visitAiError}</p>
            )}
            {visitOutputVisible && (
              <>
                <div className="flex items-center justify-between gap-2">
                  <label className="block text-sm font-medium text-gray-700">İş Çıktısı Özeti</label>
                  <button
                    type="button"
                    className="text-xs font-semibold text-brand-600 disabled:opacity-50"
                    disabled={visitPreviewLoading || visitDetailLoading || loading}
                    onClick={() => generateVisitOutput('detailed')}
                  >
                    {visitDetailLoading ? 'Detaylandırılıyor...' : 'Detaylandır'}
                  </button>
                </div>
                <div className="relative">
                  {(visitPreviewLoading || visitDetailLoading) && (
                    <div className="absolute inset-0 z-10 bg-white/70 flex items-center justify-center rounded-xl border border-gray-200">
                      <svg className="animate-spin h-6 w-6 text-brand-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    </div>
                  )}
                  <textarea
                    className="input min-h-[120px] resize-none"
                    style={{ fontSize: '16px' }}
                    placeholder="AI özeti burada görünür; düzenleyebilirsiniz."
                    value={visitOutputText}
                    onChange={(e) => setVisitOutputText(e.target.value)}
                  />
                </div>
                {visitAiError && <p className="text-xs text-amber-700 mt-1">{visitAiError}</p>}
              </>
            )}
          </div>
        )}

        {/* Sorumlu */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Sorumlu <span className="text-red-500">*</span>
          </label>
          <select
            className="input"
            style={{ fontSize: '16px' }}
            {...register('assignee_id', { required: 'Sorumlu seçiniz' })}
          >
            <option value="">Kişi seçin</option>
            {users.map(u => (
              <option key={u.id} value={u.id}>
                {u.full_name}{u.is_on_leave ? ' (İzinde)' : ''}
              </option>
            ))}
          </select>
          {errors.assignee_id && <p className="text-xs text-red-500 mt-1">{errors.assignee_id.message}</p>}
          {selectedUser?.is_on_leave && (
            <div className="mt-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-xs text-amber-700">
              Bu kişi şu an izinde. Görevi yine de atayabilirsiniz.
            </div>
          )}
        </div>

        {/* Deadline */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {isCustomerVisit ? 'Müşteri Ziyaret Tarihi' : 'Hedef Bitiş Tarihi'}{' '}
            <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            className="input"
            style={{ fontSize: '16px' }}
            min={new Date().toISOString().split('T')[0]}
            {...register('deadline', { required: 'Tarih zorunludur' })}
          />
          {errors.deadline && <p className="text-xs text-red-500 mt-1">{errors.deadline.message}</p>}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {!isCustomerVisit && (
          <button type="submit" className="btn-primary" disabled={loading || !parentType}>
            {loading ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        )}
        {isCustomerVisit && visitOutputVisible && (
          <button
            type="button"
            className="btn-primary"
            disabled={loading || !parentType || visitOutputText.trim().length < 10}
            onClick={handleSubmit(onVisitSaveAndComplete)}
          >
            {loading ? 'Kaydediliyor...' : 'Kaydet ve Tamamlandı'}
          </button>
        )}
        <button
          type="button"
          className="btn-secondary"
          onClick={() => (onCancel ? onCancel() : navigate(-1))}
        >
          İptal
        </button>
      </form>

      {categoryModalOpen && (
        <div
          className="fixed inset-0 z-[80] bg-black/40 flex items-end sm:items-center"
          onClick={() => setCategoryModalOpen(false)}
        >
          <div
            className="bg-white w-full max-w-md mx-auto rounded-t-2xl sm:rounded-2xl flex flex-col overflow-hidden"
            style={{ maxHeight: 'min(75dvh, calc(100svh - env(safe-area-inset-top, 0px) - 2rem))' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 pt-4 pb-3 overflow-y-auto min-h-0" style={{ flex: '0 1 auto' }}>
              <h3 className="font-semibold text-gray-900 mb-1">Kategori Talebi</h3>
              <p className="text-xs text-gray-500 mb-3">
                Talep, <b>{parentType === 'customer' ? 'Müşteri İşleri' : 'Genel İşler'}</b> altında oluşturulacak.
              </p>
              <input
                className="input"
                style={{ fontSize: '16px' }}
                placeholder="Örn: Genel Görüşme"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
              />
              {categoryMessage && (
                <p className={`text-xs mt-2 ${categoryMessageType === 'error' ? 'text-red-600' : 'text-green-600'}`}>
                  {categoryMessage}
                </p>
              )}
            </div>
            <div className="px-4 pt-3 pb-6 flex flex-col gap-2 border-t border-gray-100 shrink-0 bg-white">
              <button
                type="button"
                className="btn-primary"
                onClick={submitCategoryRequest}
                disabled={categorySubmitting}
              >
                {categorySubmitting ? 'Gönderiliyor...' : 'Talep Gönder'}
              </button>
              <button type="button" className="btn-secondary" onClick={() => setCategoryModalOpen(false)}>
                İptal
              </button>
            </div>
          </div>
        </div>
      )}

      {customerModalOpen && (
        <div className="fixed inset-0 z-[80] bg-black/40 flex items-end sm:items-center">
          <div
            className="bg-white w-full max-w-md mx-auto rounded-t-2xl sm:rounded-2xl flex flex-col overflow-hidden"
            style={{ maxHeight: 'min(75dvh, calc(100svh - env(safe-area-inset-top, 0px) - 2rem))' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 pt-4 pb-3 overflow-y-auto min-h-0" style={{ flex: '0 1 auto' }}>
              <h3 className="font-semibold text-gray-900 mb-3">Yeni Müşteri</h3>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Piyasa <span className="text-red-500">*</span>
              </label>
              <div className="flex rounded-xl border border-gray-200 overflow-hidden mb-3">
                <button
                  type="button"
                  onClick={() => setNewCustomerIsDomestic(true)}
                  className={`flex-1 py-2.5 px-2 text-sm font-medium border-r border-gray-200 transition-colors
                    ${newCustomerIsDomestic === true ? 'bg-brand-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                  Yurtiçi
                </button>
                <button
                  type="button"
                  onClick={() => setNewCustomerIsDomestic(false)}
                  className={`flex-1 py-2.5 px-2 text-sm font-medium transition-colors
                    ${newCustomerIsDomestic === false ? 'bg-brand-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                  Yurtdışı
                </button>
              </div>
              <input
                className="input"
                style={{ fontSize: '16px' }}
                placeholder="Müşteri adı"
                value={newCustomerName}
                onChange={(e) => setNewCustomerName(e.target.value)}
              />
              <div className="mt-2 min-h-5">
                {checkingDuplicate && <p className="text-xs text-gray-400">Benzer kayıtlar kontrol ediliyor...</p>}
                {!checkingDuplicate && duplicateList.length > 0 && (
                  <p className="text-xs text-amber-700">
                    Benzer isimler: {duplicateList.map((d) => d.name).join(', ')}. Yine de ekleyebilirsiniz.
                  </p>
                )}
              </div>
              {customerMessage && <p className="text-xs text-red-600 mt-1">{customerMessage}</p>}
            </div>
            <div className="px-4 pt-3 pb-6 flex flex-col gap-2 border-t border-gray-100 shrink-0 bg-white">
              <button
                type="button"
                className="btn-primary"
                onClick={submitCustomer}
                disabled={customerSubmitting}
              >
                {customerSubmitting ? 'Kaydediliyor...' : 'Müşteri Ekle'}
              </button>
              <button type="button" className="btn-secondary" onClick={closeCustomerModal}>
                İptal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin — Kategori Yönetimi Modal */}
      {catMgmtOpen && (
        <div className="fixed inset-0 z-[80] bg-black/40 flex items-end sm:items-center"
          onClick={() => { setCatMgmtOpen(false); setEditingCatId(null); setDeletingCatId(null); }}>
          <div
            className="bg-white w-full max-w-md mx-auto rounded-t-2xl sm:rounded-2xl px-4 pt-4 pb-[max(1rem,env(safe-area-inset-bottom,0px))] max-h-[80vh] min-h-0 overflow-y-auto flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900">Kategorileri Düzenle</h3>
              <span className="text-xs text-gray-400">
                {parentType ? (parentType === 'customer' ? 'Müşteri İşleri' : 'Genel İşler') : 'Tüm kategoriler'}
              </span>
            </div>

            {catMgmtError && (
              <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 mb-2">
                {catMgmtError}
              </p>
            )}

            {/* Silme onayı */}
            {deletingCatId && (() => {
              const cat = categories.find(c => c.id === deletingCatId);
              return (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-3 space-y-2">
                  <p className="text-sm font-semibold text-red-800">Kategori Silinsin mi?</p>
                  <p className="text-xs text-red-700">
                    <b>"{cat?.name}"</b> kategorisi silinecek.
                    {cat?.task_count > 0 && (
                      <span className="ml-1">
                        Bu kategoriyi kullanan <b>{cat.task_count} iş</b> var —
                        ilgili kişilere bildirim gönderilecek ve yeni kategori seçmeleri istenecek.
                      </span>
                    )}
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="px-3 py-1.5 bg-red-500 text-white text-xs font-semibold rounded-lg disabled:opacity-50"
                      disabled={catMgmtBusy[deletingCatId]}
                      onClick={deleteCategory}
                    >
                      {catMgmtBusy[deletingCatId] ? 'Siliniyor…' : 'Evet, Sil'}
                    </button>
                    <button
                      type="button"
                      className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-semibold rounded-lg"
                      onClick={() => setDeletingCatId(null)}
                    >
                      Vazgeç
                    </button>
                  </div>
                </div>
              );
            })()}

            <div className="overflow-y-auto flex-1 min-h-0 space-y-1">
              {categories.filter(c => !parentType || c.parent_type === parentType).map(cat => (
                <div key={cat.id} className="flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-gray-50">
                  {editingCatId === cat.id ? (
                    <>
                      <input
                        className="input flex-1 py-1.5"
                        style={{ fontSize: '16px' }}
                        value={editingCatName}
                        onChange={e => setEditingCatName(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && saveEditCat(cat.id)}
                        autoFocus
                      />
                      <button
                        type="button"
                        className="px-2 py-1 bg-brand-500 text-white text-xs font-semibold rounded-lg shrink-0 disabled:opacity-50"
                        disabled={catMgmtBusy[cat.id]}
                        onClick={() => saveEditCat(cat.id)}
                      >
                        {catMgmtBusy[cat.id] ? '…' : 'Kaydet'}
                      </button>
                      <button
                        type="button"
                        className="px-2 py-1 bg-gray-100 text-gray-600 text-xs font-semibold rounded-lg shrink-0"
                        onClick={() => setEditingCatId(null)}
                      >
                        İptal
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="flex-1 text-sm text-gray-800 truncate">
                        {cat.name}
                        {!cat.is_active && <span className="ml-1 text-[10px] text-amber-600">(onay bekliyor)</span>}
                        {cat.task_count > 0 && (
                          <span className="ml-1 text-[10px] text-gray-400">{cat.task_count} iş</span>
                        )}
                      </span>
                      <button
                        type="button"
                        title="İsim değiştir"
                        className="p-1 text-gray-400 hover:text-brand-600 shrink-0"
                        onClick={() => startEditCat(cat)}
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round"
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        title="Sil"
                        className="p-1 text-gray-400 hover:text-red-500 shrink-0"
                        onClick={() => confirmDeleteCat(cat)}
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round"
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </>
                  )}
                </div>
              ))}
              {categories.filter(c => !parentType || c.parent_type === parentType).length === 0 && (
                <p className="text-sm text-gray-400 text-center py-6">Bu türde kategori yok.</p>
              )}
            </div>

            <button
              type="button"
              className="btn-secondary mt-3 shrink-0"
              onClick={() => { setCatMgmtOpen(false); setEditingCatId(null); setDeletingCatId(null); }}
            >
              Kapat
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
